import { PageMeta } from "@/components/PageMeta";
import { GlassPanel } from "@/components/GlassPanel";
import { StatusBadge } from "@/components/StatusBadge";
import { CtaLink } from "@/components/Button";
import { siteConfig } from "@/config/site";
import { routeSeo } from "@/content/seo";
import type { FeatureStatus } from "@/types/content";

/**
 * The programme phases a visitor is most likely to ask about, in order. Status
 * uses the same fixed vocabulary as everywhere else on the site (`FeatureStatus`)
 * — never a bespoke set of labels for this one section.
 */
const developmentStages: { title: string; status: FeatureStatus; body: string }[] = [
  {
    title: "Platform foundations",
    status: "pilot",
    body: "The graph-native composition model and local Runtime execution engine are real and exercised inside the pilot.",
  },
  {
    title: "Cloud application and Gateway integration",
    status: "live",
    body: "Devory Cloud's backend services and API Gateway are live in production: real identity, organisations, Stripe billing and a scoped cloud-execution path.",
  },
  {
    title: "Desktop and Cloud unification",
    status: "in-development",
    body: "Connecting the local Desktop canvas to Devory Cloud into one consistent experience is active work; project import/push/pull between the two is real, broader unification continues.",
  },
  {
    title: "Trusted internal-pilot qualification",
    status: "live",
    body: "The internal pilot validated a full, real end-to-end walkthrough and exited into Public Release 0.1.",
  },
  {
    title: "Public Release 0.1",
    status: "live",
    body: "Self-serve sign-up, real organisations and billing, scoped cloud execution and marketplace browsing are open to everyone today.",
  },
  {
    title: "Broader general availability",
    status: "in-development",
    body: "Wider execution coverage, a populated public Marketplace catalogue, and enterprise governance features beyond the current self-serve surface.",
  },
];

export function AboutPage() {
  const seo = routeSeo["/about"];
  return (
    <>
      <PageMeta title={seo.title} description={seo.description} path="/about" />

      <section className="container page-hero">
        <p className="eyebrow">About</p>
        <h1 className="page-hero-title">Why Devory exists</h1>
        <p className="page-hero-lede">{siteConfig.tagline}</p>
      </section>

      <section className="container-narrow section prose">
        <h2>Our thesis</h2>
        <p>
          Modern engineering work is split across disconnected tools: a design or diagramming tool,
          a code editor, a terminal, a source-control client, cloud dashboards and deployment
          systems, often with a separate AI assistant bolted on top. Each tool has its own model of
          the system, and none of them stay in sync with each other for long.
        </p>
        <p>
          Devory's thesis is that these activities belong in one coherent, inspectable engineering
          ecosystem: a visual canvas of composable tiles, connected to real execution, real source
          control and real deployment, not a diagram that only looks like the system.
        </p>

        <h2>Current stage</h2>
        <p>
          {siteConfig.stage.summary} Devory is developed by {siteConfig.legalDeveloper}. This
          website exists to explain the platform honestly at every stage, rather than only once it
          is finished.
        </p>
        <ol className="stage-list">
          {developmentStages.map((stage) => (
            <li key={stage.title} className="stage-list-item">
              <div className="stage-list-item-head">
                <h3>{stage.title}</h3>
                <StatusBadge status={stage.status} />
              </div>
              <p>{stage.body}</p>
            </li>
          ))}
        </ol>
        <h2>Get in touch</h2>
        <p>
          For product questions, partnership interest, or anything else, use the contact form or
          email <a href={`mailto:${siteConfig.contactEmail}`}>{siteConfig.contactEmail}</a>.
        </p>
      </section>

      <section className="container section-tight">
        <GlassPanel className="cta-banner">
          <h2>Want to know more?</h2>
          <p>Explore what the platform does today, or reach out directly.</p>
          <div className="cta-banner-actions">
            <CtaLink to="/product" variant="secondary">
              Explore the platform
            </CtaLink>
            <CtaLink to="/contact" variant="primary">
              Contact Devory
            </CtaLink>
          </div>
        </GlassPanel>
      </section>
    </>
  );
}
